/**
 * hatif repository — Realtime subscriptions for `public.hatif_webhook_events`.
 * Centralises the Supabase channel access so hooks don't reach into
 * `supabase.channel()` directly — same convention as `messagesRepository` etc.
 *
 * One subscription shape: `subscribeHatifCompanyEvents({ companyId, onInsert })`
 * — filtered to one company and mounted once at the authed shell, so a single
 * tab-level sub fans out to every cached conversation rather than one channel
 * per open thread. `useHatifWebhookCacheRouter` (and the Conversations inbox)
 * consume it.
 *
 * (A narrower per-conversation `subscribeHatifWebhookEvents` existed until
 * 2026-07-26. `useWhatsAppConversation` stopped calling it when the router took
 * over, leaving it with zero consumers, so it was deleted rather than kept "for
 * backward compat".)
 *
 * (The `hatif_conversations` index that this file used to read/subscribe was
 * removed once the inbox + thread lookup moved to live Hatif reads.)
 */
import { subscribeTableRows } from '@/lib/realtime/subscribeTableRows';

import type { HatifWebhookEventRow } from '@/lib/hatif/extractRoutingKey';

export interface SubscribeHatifCompanyEventsParams {
  /** Company whose webhooks we want to listen to. The RLS SELECT policy on
   * `hatif_webhook_events` already restricts a user to their companies, so
   * filtering here is for cost (less traffic across the WS) and ergonomics
   * (callbacks know the row is in-scope without revalidating). */
  companyId: string;
  /** Receives the inserted row directly. The router downstream reads
   * `event_type` + `conversation_id` to decide which (if any) cache
   * subtree to sync. */
  onInsert: (row: HatifWebhookEventRow) => void;
}

/**
 * Subscribe to every `hatif_webhook_events` INSERT for a given company.
 * One channel per signed-in user, mounted at the authed shell so it
 * survives page navigation. Unsubscribe on logout / unmount.
 */
export function subscribeHatifCompanyEvents({
  companyId,
  onInsert,
}: SubscribeHatifCompanyEventsParams): () => void {
  return subscribeTableRows<HatifWebhookEventRow>({
    channelName: `hatif_webhook_events:company:${companyId}`,
    table: 'hatif_webhook_events',
    filter: `company_id=eq.${companyId}`,
    onInsert,
  });
}

export const hatifRepository = {
  subscribeCompanyEvents: subscribeHatifCompanyEvents,
};
